/**
 * Отступы первого/последнего контура распределительного коллектора (мм) по гребёнке и патрубкам.
 * Торцевые контуры не учитываются.
 */

import fittingsData from '../fittingsData';
import { getTransitionPipeDiameter } from './endConnectionVelocitySizing';

/** Суффикс ключа Nippel_* / Mufta_* → номинальный ⌀ трубы (мм), как у ниппеля. */
const NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM = {
  '1.4': 13.7,
  '3.8': 17.2,
  '1.2': 21.3,
  '3.4': 26.9,
  '1': 33.7,
  '1_1.4': 42.4,
  '1_1.2': 48.3,
  '2': 60.3,
  '2_1/2': 76.1,
  '3': 88.9,
  '4': 114.3,
};

function nominalPipeMmFromNippleOrMuftaId(fittingId) {
  const m = fittingId.match(/^Nippel_(.+)$/) || fittingId.match(/^Mufta_(.+)$/);
  if (!m) return null;
  const mm = NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM[m[1]];
  return mm != null ? mm : null;
}

function refPipeMmForCircuitFitting(fittingId, fittingCategory) {
  if (fittingCategory === 'Ниппели' || fittingCategory === 'Муфты') {
    const fromSuffix = nominalPipeMmFromNippleOrMuftaId(fittingId);
    if (fromSuffix != null) return fromSuffix;
    const d = Number(fittingsData[fittingId]?.diameter);
    return Number.isFinite(d) ? d : null;
  }
  if (fittingCategory === 'Накидные гайки') {
    const m = fittingId.match(/^Nak_Gaika_(.+)$/);
    if (!m) return null;
    const threadNominal = NIPPLE_MUFTA_SUFFIX_TO_NOMINAL_PIPE_MM[m[1]];
    if (threadNominal == null) return null;
    return getTransitionPipeDiameter('nut', threadNominal);
  }
  if (fittingCategory === 'Шайбы с наружной резьбой') {
    // В каталоге у Shaib_Nar_Rezba_* в diameter — номинал присоединяемой трубы (33.7, 42.4, …)
    const key = Number(fittingsData[fittingId]?.diameter);
    if (!Number.isFinite(key)) return null;
    return getTransitionPipeDiameter('washer', key);
  }
  return null;
}

function edgeOffsetFromNominalPipeMm(refMm) {
  if (!Number.isFinite(refMm)) return 109;
  if (refMm <= 33.7) return 109;
  if (refMm <= 42.4) return 120; // 1 1/4"
  if (refMm <= 48.3) return 130; // 1 1/2"
  if (refMm <= 60.3) return 135;
  if (refMm <= 76.1) return 145;
  if (refMm <= 88.9) return 155;
  if (refMm <= 114.3) return 170;
  return 170;
}

function internalSpacingFromNominalPipeMm(refMm) {
  if (!Number.isFinite(refMm)) return 125;
  if (refMm <= 26.9) return 90;
  if (refMm <= 33.7) return 125;
  if (refMm <= 42.4) return 125;
  if (refMm <= 48.3) return 200;
  if (refMm <= 60.3) return 250;
  if (refMm <= 76.1) return 300;
  if (refMm <= 88.9) return 300;
  if (refMm <= 114.3) return 300;
  return 300;
}

const FLANGE_DN_TO_EDGE_OFFSET_MM = {
  25: 165,
  32: 175,
  40: 180,
  50: 185,
  65: 195,
  80: 205,
  100: 215,
  125: 230,
  150: 250,
  200: 275,
  250: 305,
  300: 330,
  350: 360,
};

/**
 * Крупный корпус гребёнки: круг ⌀ > 60.3 или квадрат со стороной > 60 (60×60 ≈ ⌀ 60.3 по порогу).
 * Отступ первого/последнего контура не ниже этого значения (мм).
 */
const DISTRIBUTION_MIN_FIRST_LAST_OFFSET_LARGE_MANIFOLD_MM = 120;

/**
 * Отступ от края гребёнки до центра одного контура по фитингу контура.
 */
export function getDistributionCircuitContourEdgeOffsetMm(fittingId, fittingCategory) {
  if (!fittingId || !fittingCategory) return 109;

  if (fittingCategory === 'Фланцы') {
    const m = fittingId.match(/^Flanec_(\d+)_/);
    if (m) {
      const dn = Number(m[1]);
      if (FLANGE_DN_TO_EDGE_OFFSET_MM[dn] != null) return FLANGE_DN_TO_EDGE_OFFSET_MM[dn];
    }
    return 215;
  }

  if (
    fittingCategory === 'Ниппели' ||
    fittingCategory === 'Муфты' ||
    fittingCategory === 'Накидные гайки' ||
    fittingCategory === 'Шайбы с наружной резьбой'
  ) {
    const refMm = refPipeMmForCircuitFitting(fittingId, fittingCategory);
    return edgeOffsetFromNominalPipeMm(refMm);
  }

  return 109;
}

/**
 * @param {object} p
 * @param {Array<{ fitting?: string, fittingCategory?: string }>} p.supplyCircuits
 * @param {Array<{ fitting?: string, fittingCategory?: string }>} p.returnCircuits
 * @param {number|null} p.mainPipeOuterMm — наружный ⌀ гребёнки (круг), мм
 * @param {boolean} p.isSquare
 * @param {number|null} p.profileSideMm — сторона квадратного профиля (из ключа «150x150»), мм
 *
 * Порог «малый / крупный» корпус: для квадрата сторона ≤60 (60×60) соответствует кругу ⌀≤60.3; сторона >60 — как ⌀>60.3.
 */
export function getDistributionCollectorFirstLastOffsetsMm({
  supplyCircuits,
  returnCircuits,
  mainPipeOuterMm,
  isSquare,
  profileSideMm,
}) {
  const mainPipeSmall =
    (!isSquare && mainPipeOuterMm != null && mainPipeOuterMm <= 60.3) ||
    (isSquare && profileSideMm != null && profileSideMm <= 60);

  const edgeFromIndex = (idxS, idxR) => {
    const values = [];
    if (mainPipeSmall) values.push(109);
    if (idxS >= 0 && supplyCircuits[idxS]?.fitting) {
      values.push(
        getDistributionCircuitContourEdgeOffsetMm(
          supplyCircuits[idxS].fitting,
          supplyCircuits[idxS].fittingCategory
        )
      );
    }
    if (idxR >= 0 && returnCircuits[idxR]?.fitting) {
      values.push(
        getDistributionCircuitContourEdgeOffsetMm(
          returnCircuits[idxR].fitting,
          returnCircuits[idxR].fittingCategory
        )
      );
    }
    return values.length > 0 ? Math.max(...values) : 109;
  };

  const sn = supplyCircuits.length;
  const rn = returnCircuits.length;

  let firstOffset = edgeFromIndex(sn > 0 ? 0 : -1, rn > 0 ? 0 : -1);
  let lastOffset = edgeFromIndex(
    sn > 0 ? sn - 1 : -1,
    rn > 0 ? rn - 1 : -1
  );

  const largeManifold =
    (!isSquare && mainPipeOuterMm != null && mainPipeOuterMm > 60.3) ||
    (isSquare && profileSideMm != null && profileSideMm > 60);
  if (largeManifold) {
    const lo = DISTRIBUTION_MIN_FIRST_LAST_OFFSET_LARGE_MANIFOLD_MM;
    firstOffset = Math.max(firstOffset, lo);
    lastOffset = Math.max(lastOffset, lo);
  }

  return { firstOffset, lastOffset };
}

/**
 * Отступы первого/последнего контура для узла «коллектор + гидрострелка» (мм).
 * Правила отдельные от распределительного коллектора: для первой колонки берётся max(first),
 * для последней — max(last) по подающему и обратному контуру этой колонки.
 */
const CWS_FLANGE_DN_TO_FIRST_LAST_MM = {
  25: [125, 165],
  32: [150, 175],
  40: [150, 180],
  50: [160, 185],
  65: [160, 195],
  80: [160, 205],
  100: [160, 215],
  125: [160, 230],
  150: [160, 250],
  200: [210, 275],
  250: [235, 305],
  300: [260, 330],
  350: [285, 360],
};

function collectorWithSeparatorPairForFitting(fittingId, fittingCategory, mainPipeSmall) {
  const baseSmall = { first: 65, last: 50 };
  const baseLarge = { first: 115, last: 120 };

  if (!fittingId || !fittingCategory) {
    return mainPipeSmall ? baseSmall : baseLarge;
  }

  if (fittingCategory === 'Фланцы') {
    const m = fittingId.match(/^Flanec_(\d+)_/);
    if (m) {
      const dn = Number(m[1]);
      const fl = CWS_FLANGE_DN_TO_FIRST_LAST_MM[dn];
      if (fl) return { first: fl[0], last: fl[1] };
    }
    return { first: 160, last: 215 };
  }

  if (
    fittingCategory === 'Ниппели' ||
    fittingCategory === 'Муфты' ||
    fittingCategory === 'Накидные гайки' ||
    fittingCategory === 'Шайбы с наружной резьбой'
  ) {
    const refMm = refPipeMmForCircuitFitting(fittingId, fittingCategory);
    if (!Number.isFinite(refMm)) {
      return mainPipeSmall ? baseSmall : baseLarge;
    }
    if (refMm <= 33.7) {
      return mainPipeSmall ? baseSmall : baseLarge;
    }
    if (refMm <= 42.4) return { first: 120, last: 120 }; // 1 1/4"
    if (refMm <= 48.3) return { first: 130, last: 130 }; // 1 1/2"
    if (refMm <= 60.3) return { first: 150, last: 135 };
    if (refMm <= 76.1) return { first: 150, last: 145 };
    if (refMm <= 88.9) return { first: 150, last: 155 };
    if (refMm <= 114.3) return { first: 150, last: 170 };
    return { first: 150, last: 170 };
  }

  return mainPipeSmall ? baseSmall : baseLarge;
}

/**
 * Порог малый/крупный корпус гребёнки: сторона ≤60 (60×60) ≈ ⌀≤60.3; сторона >60 ≈ ⌀>60.3.
 */
export function getCollectorWithSeparatorFirstLastOffsetsMm({
  supplyCircuits,
  returnCircuits,
  mainPipeOuterMm,
  isSquare,
  profileSideMm,
}) {
  const mainPipeSmall =
    (!isSquare && mainPipeOuterMm != null && mainPipeOuterMm <= 60.3) ||
    (isSquare && profileSideMm != null && profileSideMm <= 60);

  const defaultPair = mainPipeSmall ? { first: 65, last: 50 } : { first: 115, last: 120 };

  const maxFirstAt = (idxS, idxR) => {
    const firsts = [];
    if (idxS >= 0 && supplyCircuits[idxS]?.fitting) {
      firsts.push(
        collectorWithSeparatorPairForFitting(
          supplyCircuits[idxS].fitting,
          supplyCircuits[idxS].fittingCategory,
          mainPipeSmall
        ).first
      );
    }
    if (idxR >= 0 && returnCircuits[idxR]?.fitting) {
      firsts.push(
        collectorWithSeparatorPairForFitting(
          returnCircuits[idxR].fitting,
          returnCircuits[idxR].fittingCategory,
          mainPipeSmall
        ).first
      );
    }
    return firsts.length > 0 ? Math.max(...firsts) : defaultPair.first;
  };

  const maxLastAt = (idxS, idxR) => {
    const lasts = [];
    if (idxS >= 0 && supplyCircuits[idxS]?.fitting) {
      lasts.push(
        collectorWithSeparatorPairForFitting(
          supplyCircuits[idxS].fitting,
          supplyCircuits[idxS].fittingCategory,
          mainPipeSmall
        ).last
      );
    }
    if (idxR >= 0 && returnCircuits[idxR]?.fitting) {
      lasts.push(
        collectorWithSeparatorPairForFitting(
          returnCircuits[idxR].fitting,
          returnCircuits[idxR].fittingCategory,
          mainPipeSmall
        ).last
      );
    }
    return lasts.length > 0 ? Math.max(...lasts) : defaultPair.last;
  };

  const sn = supplyCircuits.length;
  const rn = returnCircuits.length;

  let firstOffset = maxFirstAt(sn > 0 ? 0 : -1, rn > 0 ? 0 : -1);
  let lastOffset = maxLastAt(sn > 0 ? sn - 1 : -1, rn > 0 ? rn - 1 : -1);

  // Как у распределительного коллектора: крупный корпус (⌀>60.3 / сторона >60) — не ниже 120 мм.
  const largeManifold =
    (!isSquare && mainPipeOuterMm != null && mainPipeOuterMm > 60.3) ||
    (isSquare && profileSideMm != null && profileSideMm > 60);
  if (largeManifold) {
    const lo = DISTRIBUTION_MIN_FIRST_LAST_OFFSET_LARGE_MANIFOLD_MM;
    firstOffset = Math.max(firstOffset, lo);
    lastOffset = Math.max(lastOffset, lo);
  }

  return { firstOffset, lastOffset };
}

/** Межосевое внутри контура (мм) по фитингу: таблица для распределительного коллектора. */
const FLANGE_DN_TO_INTERNAL_SPACING_MM = {
  25: 125,
  32: 150,
  40: 200,
  50: 300,
  65: 300,
  80: 300,
  100: 300,
  125: 300,
  150: 300,
  200: 400,
  250: 450,
  300: 500,
  350: 550,
};

/**
 * Базовое «межосевое контура» (мм) для одного контура по его фитингу.
 */
export function getDistributionCircuitInternalSpacingBaseMm(fittingId, fittingCategory) {
  if (!fittingId || !fittingCategory) return 125;

  if (fittingCategory === 'Фланцы') {
    const m = fittingId.match(/^Flanec_(\d+)_/);
    if (m) {
      const dn = Number(m[1]);
      if (FLANGE_DN_TO_INTERNAL_SPACING_MM[dn] != null) {
        return FLANGE_DN_TO_INTERNAL_SPACING_MM[dn];
      }
    }
    return 300;
  }

  if (
    fittingCategory === 'Ниппели' ||
    fittingCategory === 'Муфты' ||
    fittingCategory === 'Накидные гайки' ||
    fittingCategory === 'Шайбы с наружной резьбой'
  ) {
    const refMm = refPipeMmForCircuitFitting(fittingId, fittingCategory);
    return internalSpacingFromNominalPipeMm(refMm);
  }

  return 125;
}

/** Округление межосевого до 5 мм (162.5 → 165). */
export function roundContourSpacingToStep5(mm) {
  if (!Number.isFinite(mm)) return 125;
  return Math.round(mm / 5) * 5;
}

/**
 * Для ряда контуров: базовое межосевое на каждый контур и «След.» между i и i+1 =
 * round5((base[i] + base[i+1]) / 2); у последнего контура spacingToNext = null.
 *
 * @param {Array<{ fitting?: string, fittingCategory?: string }>} circuits
 * @returns {{ bases: number[], spacingToNext: (number|null)[] }}
 */
export function computeDistributionCircuitSpacingLayout(circuits) {
  const n = circuits.length;
  const bases = circuits.map((c) =>
    getDistributionCircuitInternalSpacingBaseMm(c.fitting, c.fittingCategory)
  );
  const spacingToNext = bases.map((_, i) => {
    if (i >= n - 1) return null;
    return roundContourSpacingToStep5((bases[i] + bases[i + 1]) / 2);
  });
  return { bases, spacingToNext };
}
